import { useState } from "react";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, CheckCircle2, XCircle, Wallet, Printer } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const MODES = ["BANK_TRANSFER", "NEFT", "RTGS", "IMPS", "UPI", "CHEQUE", "CASH", "OTHER"];

export default function CustomerReceipts() {
  const { data: buyersRaw } = useResource("/buyers");
  const buyers = Array.isArray(buyersRaw) ? buyersRaw : [];
  const [filterBuyer, setFilterBuyer] = useState("");
  const { data: receiptsRaw, refetch } = useResource(filterBuyer ? `/customer-receipts?buyerId=${filterBuyer}` : "/customer-receipts");
  const receipts = Array.isArray(receiptsRaw) ? receiptsRaw : [];

  const [form, setForm] = useState({ buyerId: "", amount: "", paymentMode: "UPI", reference: "", receiptDate: new Date().toISOString().slice(0, 10), narration: "" });
  const [busy, setBusy] = useState(false);
  const [allocOpen, setAllocOpen] = useState(null); // receipt being allocated
  const [invoices, setInvoices] = useState([]);
  const [alloc, setAlloc] = useState({}); // {invoiceId: {cash, tds}}

  const create = async () => {
    if (!form.buyerId) return toast.error("Select buyer");
    if (!(Number(form.amount) > 0)) return toast.error("Enter amount");
    setBusy(true);
    try { await api.post("/customer-receipts", form); toast.success("Receipt created (DRAFT)"); setForm({ ...form, amount: "", reference: "", narration: "" }); refetch(); }
    catch (e) { toast.error(apiErrorMessage(e, "Create failed")); } finally { setBusy(false); }
  };
  const post = async (r) => { try { const res = await api.post(`/customer-receipts/${r.id}/post`, {}); toast.success(res.data.idempotentReplay ? "Already posted" : "Receipt posted"); refetch(); } catch (e) { toast.error(apiErrorMessage(e, "Post failed")); } };
  const printVoucher = async (r) => {
    try {
      const res = await api.get(`/customer-receipts/${r.id}/voucher`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `receipt-voucher-${r.receiptNo}.pdf`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not print voucher")); }
  };
  const cancel = async (r) => { const reason = window.prompt("Cancellation reason?") || ""; try { await api.post(`/customer-receipts/${r.id}/cancel`, { reason }); toast.success("Receipt cancelled"); refetch(); } catch (e) { toast.error(apiErrorMessage(e, "Cancel failed")); } };

  const openAllocate = async (r) => {
    setAllocOpen(r); setAlloc({});
    try { const { data } = await api.get(`/buyers/${r.buyerId}/open-invoices`); setInvoices(Array.isArray(data) ? data : []);
      const seed = {}; (r.allocations || []).forEach((a) => { seed[a.invoiceId] = { cash: a.amount, tds: a.tds || "" }; }); setAlloc(seed);
    } catch (e) { toast.error(apiErrorMessage(e, "Load invoices failed")); }
  };

  const activeBuyer = buyers.find((b) => (b.buyerId || b.id) === allocOpen?.buyerId);
  const buyerTdsRate = activeBuyer?.tdsApplicable ? Number(activeBuyer?.tdsRate || 0) : 0;

  const setCash = (invId, val) => {
    const cur = alloc[invId] || { cash: "", tds: "" };
    const next = { ...alloc, [invId]: { ...cur, cash: val } };
    // Buyer-level TDS is only a DEFAULT suggestion — editable, never forced.
    if (buyerTdsRate > 0 && (cur.tds === "" || cur.tds == null) && Number(val) > 0) {
      next[invId].tds = Number((Number(val) * buyerTdsRate / 100).toFixed(2));
    }
    setAlloc(next);
  };
  const setTds = (invId, val) => {
    const cur = alloc[invId] || { cash: "", tds: "" };
    setAlloc({ ...alloc, [invId]: { ...cur, tds: val } });
  };

  const saveAlloc = async () => {
    const allocations = Object.entries(alloc)
      .map(([invoiceId, v]) => ({
        invoiceId, amount: Number(v.cash) || 0, tds: Number(v.tds) || 0,
        ...(Number(v.tds) > 0 && buyerTdsRate > 0 ? { tdsRate: buyerTdsRate } : {}),
      }))
      .filter((a) => a.amount > 0 || a.tds > 0);
    try { await api.put(`/customer-receipts/${allocOpen.id}/allocate`, { allocations }); toast.success("Allocation saved"); setAllocOpen(null); refetch(); }
    catch (e) { toast.error(apiErrorMessage(e, "Allocation failed")); }
  };
  const autoAllocate = async () => {
    try {
      const { data } = await api.post(`/customer-receipts/${allocOpen.id}/auto-allocate?preview=true`);
      const next = { ...alloc };
      (data.proposed || []).forEach((p) => {
        const cur = next[p.invoiceId] || { cash: "", tds: "" };
        next[p.invoiceId] = { cash: (Number(cur.cash) || 0) + Number(p.amount), tds: (Number(cur.tds) || 0) + Number(p.tds || 0) };
      });
      setAlloc(next);
      toast.success(`Auto-allocated ₹${(data.onAccountBefore - data.onAccountAfter).toLocaleString("en-IN")} earliest-first${buyerTdsRate > 0 ? " (TDS suggested)" : ""} · review then Save`);
    } catch (e) { toast.error(apiErrorMessage(e, "Auto-allocate failed")); }
  };

  const cashTotal = Object.values(alloc).reduce((s, v) => s + (Number(v.cash) || 0), 0);
  const tdsTotal = Object.values(alloc).reduce((s, v) => s + (Number(v.tds) || 0), 0);
  const onAccount = Math.max(0, Number(allocOpen?.amount || 0) - cashTotal);
  const anyNegativeRemaining = invoices.some((inv) => {
    const v = alloc[inv.invoiceId]; if (!v) return false;
    return inv.balance - (Number(v.cash) || 0) - (Number(v.tds) || 0) < -0.009;
  });

  const badge = (s) => ({ DRAFT: "border-amber-200 bg-amber-50 text-amber-700", POSTED: "border-emerald-200 bg-emerald-50 text-emerald-700", CANCELLED: "border-stone-200 bg-stone-100 text-stone-500" }[s]);

  return (
    <div className="space-y-6">
      <PageHeader title="Customer Receipts / Payments" subtitle="Record customer receipts and allocate them to invoices · TDS (when deducted by the buyer) settles the receivable but is not bank money" />

      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-stone-800"><Plus className="h-4 w-4 text-amber-700" /> New Receipt</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2"><Label className="text-xs">Buyer</Label>
            <select data-testid="rc-buyer" value={form.buyerId} onChange={(e) => setForm({ ...form, buyerId: e.target.value })} className="h-9 w-full rounded-md border border-stone-200 bg-white px-3 text-sm">
              <option value="">Select…</option>{buyers.map((b) => <option key={b.id} value={b.buyerId || b.id}>{b.name} · {b.buyerId}{b.tdsApplicable ? ` · TDS ${b.tdsRate}%` : ""}</option>)}
            </select></div>
          <div><Label className="text-xs">Amount (bank/cash received)</Label><Input type="number" data-testid="rc-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9" /></div>
          <div><Label className="text-xs">Mode</Label>
            <Select value={form.paymentMode} onValueChange={(v) => setForm({ ...form, paymentMode: v })}><SelectTrigger data-testid="rc-mode" className="h-9 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{MODES.map((m) => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Reference / UTR</Label><Input data-testid="rc-ref" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="h-9" /></div>
          <div><Label className="text-xs">Date</Label><Input type="date" data-testid="rc-date" value={form.receiptDate} onChange={(e) => setForm({ ...form, receiptDate: e.target.value })} className="h-9" /></div>
        </div>
        <div className="mt-3 flex justify-end"><Button data-testid="rc-create" className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={create}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Create Receipt</Button></div>
      </div>

      <div className="flex items-center gap-2">
        <Label className="text-xs">Filter buyer</Label>
        <select data-testid="rc-filter" value={filterBuyer} onChange={(e) => setFilterBuyer(e.target.value)} className="h-9 w-64 rounded-md border border-stone-200 bg-white px-3 text-sm">
          <option value="">All buyers</option>{buyers.map((b) => <option key={b.id} value={b.buyerId || b.id}>{b.name}</option>)}
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="rc-list">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-xs uppercase text-stone-500"><tr>{["Receipt", "Date", "Buyer", "Mode", "Reference", "Amount", "Allocated", "TDS", "Status", ""].map((h) => <th key={h} className="px-3 py-2.5">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-stone-100">
            {receipts.length === 0 ? <tr><td colSpan={10} className="px-3 py-6 text-center text-stone-400">No receipts.</td></tr> :
              receipts.map((r) => (
                <tr key={r.id} data-testid={`rc-row-${r.receiptNo}`} className="hover:bg-stone-50/60">
                  <td className="px-3 py-2 font-mono text-xs">{r.receiptNo}</td><td className="px-3 py-2">{r.receiptDate}</td>
                  <td className="px-3 py-2">{r.buyerName}</td><td className="px-3 py-2 text-xs">{r.paymentMode}</td>
                  <td className="px-3 py-2 text-xs text-stone-500">{r.reference}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r.amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-stone-500">{money(r.allocatedAmount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700" data-testid={`rc-tds-${r.receiptNo}`}>{r.tdsTotal ? money(r.tdsTotal) : "—"}</td>
                  <td className="px-3 py-2"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${badge(r.status)}`}>{r.status}</span></td>
                  <td className="flex gap-1 px-3 py-1.5">
                    {r.status !== "CANCELLED" && <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" data-testid={`rc-allocate-${r.receiptNo}`} onClick={() => openAllocate(r)}>Allocate</Button>}
                    {r.status === "DRAFT" && <Button size="sm" className="h-7 gap-1 bg-emerald-600 px-2 text-[11px] hover:bg-emerald-700" data-testid={`rc-post-${r.receiptNo}`} onClick={() => post(r)}><CheckCircle2 className="h-3 w-3" /> Post</Button>}
                    {r.status !== "DRAFT" && <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]" data-testid={`rc-voucher-${r.receiptNo}`} onClick={() => printVoucher(r)}><Printer className="h-3 w-3" /> Voucher</Button>}
                    {r.status !== "CANCELLED" && <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-500" data-testid={`rc-cancel-${r.receiptNo}`} onClick={() => cancel(r)}><XCircle className="h-4 w-4" /></Button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!allocOpen} onOpenChange={(o) => !o && setAllocOpen(null)}>
        <DialogContent className="bg-white sm:max-w-3xl" data-testid="rc-alloc-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet className="h-4 w-4 text-amber-700" /> Allocate Receipt {allocOpen?.receiptNo}</DialogTitle>
            <DialogDescription className="text-xs">
              Bank/cash received {money(allocOpen?.amount)}. Settlement per invoice = Net Receipt + TDS Deducted; unallocated cash remains on-account.
              {activeBuyer?.tdsApplicable
                ? <span className="ml-1 inline-block" data-testid="rc-tds-default"><Badge className="border-0 bg-amber-100 text-[10px] text-amber-800">Buyer deducts TDS @ {activeBuyer.tdsRate}% (default — editable per invoice)</Badge></span>
                : <span className="ml-1" data-testid="rc-tds-default"><Badge className="border-0 bg-stone-100 text-[10px] text-stone-600">Buyer TDS: not applicable (defaults to ₹0)</Badge></span>}
            </DialogDescription></DialogHeader>
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-sm"><thead className="bg-stone-50 text-left text-[10px] uppercase text-stone-500"><tr>{["Invoice", "Date", "Amount", "Credit Notes", "Outstanding", "TDS Deducted", "Net Receipt", "Remaining"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-stone-100">
                {invoices.map((inv) => {
                  const v = alloc[inv.invoiceId] || { cash: "", tds: "" };
                  const rem = inv.balance - (Number(v.cash) || 0) - (Number(v.tds) || 0);
                  return (
                    <tr key={inv.invoiceId}>
                      <td className="px-2 py-1.5 font-mono text-xs">{inv.invoiceNo}</td><td className="px-2 py-1.5">{inv.invoiceDate}</td>
                      <td className="px-2 py-1.5 text-right">{money(inv.amount)}</td>
                      <td className="px-2 py-1.5 text-right text-stone-500">{money(inv.creditNotes)}</td>
                      <td className="px-2 py-1.5 text-right font-semibold">{money(inv.balance)}</td>
                      <td className="px-2 py-1.5 text-right">
                        <Input type="number" data-testid={`rc-alloc-tds-${inv.invoiceNo}`} value={v.tds} onChange={(e) => setTds(inv.invoiceId, e.target.value)} className="h-7 w-20 text-right" placeholder="0" />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Input type="number" data-testid={`rc-alloc-${inv.invoiceNo}`} value={v.cash} onChange={(e) => setCash(inv.invoiceId, e.target.value)} className="h-7 w-24 text-right" />
                      </td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${rem < -0.009 ? "font-semibold text-rose-600" : "text-stone-500"}`} data-testid={`rc-alloc-rem-${inv.invoiceNo}`}>{money(rem)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3">
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="rc-auto-allocate" onClick={autoAllocate}>
              <Wallet className="h-3.5 w-3.5" /> Auto-Allocate (earliest first)
            </Button>
            <div className={`text-sm ${cashTotal > Number(allocOpen?.amount || 0) ? "text-rose-600" : "text-stone-600"}`}>
              Cash <b>{money(cashTotal)}</b> of {money(allocOpen?.amount)} · TDS <b data-testid="rc-tds-total">{money(tdsTotal)}</b> · Settlement <b data-testid="rc-settlement-total">{money(cashTotal + tdsTotal)}</b> · On Account <b data-testid="rc-on-account">{money(onAccount)}</b>
              {cashTotal > Number(allocOpen?.amount || 0) && " · cash exceeds receipt!"}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAllocOpen(null)}>Cancel</Button>
            <Button data-testid="rc-alloc-save" className="bg-emerald-600 hover:bg-emerald-700" disabled={cashTotal > Number(allocOpen?.amount || 0) || anyNegativeRemaining} onClick={saveAlloc}>Save Allocation</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
